import { eventPathCreator } from 'src/routes/employer/paths/event.paths';
import { organisationPaths } from 'src/routes/employer/paths/organisation.paths';
import { verificationPathCreator } from 'src/routes/employer/paths/verification.paths';
import { useContent } from 'src/ui/features/content/hooks/content.hook';
import { useEventsEnabled } from 'src/ui/features/events/hooks/events-enabled.hook';
import { useFormSubmissionsEnabled } from 'src/ui/features/forms/hooks/form-submissions-enabled.hook';
import { useIsJobsEnabled } from 'src/ui/features/jobs/hooks/is-jobs-enabled.hook';
import { useIsErsEnabled } from 'src/ui/features/verifications/hooks/is-ers-enabled.hook';
import { useClient } from 'src/ui/shared/hooks/client.hook';
import { contactPathCreator } from '../../../../../routes/employer/paths/contact.paths';
import { formPaths } from '../../../../../routes/employer/paths/form.paths';
import { individualPaths } from '../../../../../routes/employer/paths/individual.paths';
import { jobPathCreator } from '../../../../../routes/employer/paths/job.paths';
import { useAuthentication } from '../../../../features/authentication/hooks/authentication.hook';
import { useStrings } from '../../../hooks/strings.hook';
import { LinkWrapper, LinkWrapperOptions } from '../../router/LinkWrapper';

interface Props {
    as: LinkWrapperOptions;
    className?: string;
    hideHome?: boolean;
}

export const ApplicationNavItems = ({ as, className, hideHome }: Props): React.ReactElement => {
    const { isIndividual, isOrganisation } = useAuthentication();
    const {
        nav: { application },
    } = useStrings();
    const { enableHomeMenuItem } = useClient();
    const { firstCollection } = useContent('employerContent');
    const { isEventsEnabled } = useEventsEnabled();
    const isJobsEnabled = useIsJobsEnabled();
    const formSubmissionsEnabled = useFormSubmissionsEnabled();
    const isErsEnabled = useIsErsEnabled();

    return (
        <>
            {!hideHome && enableHomeMenuItem && (
                <LinkWrapper
                    to="/"
                    icon="house"
                    iconSize="lg"
                    as={as}
                    className={className}
                    exact={true}
                >
                    {firstCollection?.title || application.home}
                </LinkWrapper>
            )}
            {isJobsEnabled && (
                <LinkWrapper
                    to={jobPathCreator.listDefault()}
                    icon="briefcase"
                    iconSize="lg"
                    as={as}
                    className={className}
                >
                    {application.jobs}
                </LinkWrapper>
            )}

            {isIndividual && (
                <LinkWrapper
                    to={individualPaths.details}
                    icon="person"
                    iconSize="lg"
                    as={as}
                    className={className}
                >
                    {application.individualDetails}
                </LinkWrapper>
            )}

            {isOrganisation && (
                <>
                    {isEventsEnabled && (
                        <LinkWrapper
                            to={eventPathCreator.listDefault()}
                            icon="ticket"
                            iconSize="lg"
                            as={as}
                            className={className}
                        >
                            {application.events}
                        </LinkWrapper>
                    )}
                    <LinkWrapper
                        to={organisationPaths.details}
                        icon="building"
                        iconSize="lg"
                        as={as}
                        className={className}
                    >
                        {application.organisationDetails}
                    </LinkWrapper>
                    <LinkWrapper
                        to={contactPathCreator.list({ isActive: true })}
                        icon="people"
                        iconSize="lg"
                        as={as}
                        className={className}
                    >
                        {application.contacts}
                    </LinkWrapper>
                    {isErsEnabled && (
                        <LinkWrapper
                            to={verificationPathCreator.listDefault()}
                            icon="check-circle"
                            iconSize="lg"
                            as={as}
                            className={className}
                        >
                            {application.verifications}
                        </LinkWrapper>
                    )}
                </>
            )}
            {formSubmissionsEnabled && (
                <LinkWrapper
                    to={formPaths.submissions}
                    icon="clipboard"
                    iconSize="lg"
                    as={as}
                    className={className}
                >
                    {application.formSubmissions}
                </LinkWrapper>
            )}
        </>
    );
};
